// DataTable (HANDOFF §1/§4): 16-radius card; head 40 px cloud 11/600 slate;
// rows 52 (compact 40, with avatars 56); footer 48 px cloud 13/700; numeric
// columns end-aligned + tabular; selected row = teal-light.
// Narrow widths (owner addition A): the card scrolls horizontally INSIDE
// itself with a sticky first column — the page never scrolls sideways.
// Row actions stay on screen while it scrolls: the `rowActions` column and a
// last column marked `stickyEnd` stick to the inline END edge (logical
// `inset-inline-end`, so the left edge in RTL). Below a 600 px card the
// actions win and the first column scrolls away with the rest.
// Loading = skeleton rows in the table's own shape; refreshing = pulsing dot
// in the footer while the old rows stay.
//
// RULE 7: `footer` cells are API totals (e.g. the response's total_minor),
// never a client-side sum of `rows`.

import { Fragment, useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { RefreshDot, Skeleton } from './Skeleton';

export type ColumnWidth = number | 'flex' | `${number}fr`;
export type ColumnAlign = 'start' | 'end' | 'center';

export interface Column<R> {
  key: string;
  label: ReactNode;
  /** px number, 'flex' (share the rest) or 'Nfr'. Default 'flex'. */
  width?: ColumnWidth;
  align?: ColumnAlign;
  /** Cell content; default renders row[key]. */
  render?: (row: R, index: number) => ReactNode;
  /** Technical column (codes, numbers, phones): LTR isolate, Latin font. */
  ltr?: boolean;
  /** Minimum px for flex columns before the card starts to scroll (default 140). */
  minWidth?: number;
  /**
   * Row-action column: pinned to the inline-end edge while the card scrolls
   * horizontally, so «سند صرف» / ⋯ never sit off screen. Honoured on the LAST
   * column only, and only when the table has no `rowActions` (which is the
   * pinned column then).
   */
  stickyEnd?: boolean;
}

/** Spec shorthand `[label, flex|px, align]` → Column. */
export function col<R>(key: string, label: ReactNode, width: ColumnWidth = 'flex', align: ColumnAlign = 'start', render?: Column<R>['render']): Column<R> {
  return { key, label, width, align, render };
}

export type TableDensity = 'default' | 'compact' | 'avatar';

export interface DataTableProps<R> {
  columns: Column<R>[];
  rows: R[] | undefined;
  rowKey: (row: R) => string;
  'aria-label': string;
  density?: TableDensity;
  /** Footer row keyed by column key (API totals only). */
  footer?: Partial<Record<string, ReactNode>>;
  selectedKey?: string | null;
  onRowClick?: (row: R) => void;
  /** Trailing per-row actions (SmallButton or a ⋯ menu). */
  rowActions?: (row: R) => ReactNode;
  /** Width of the `rowActions` column in px (default 96) — size it to the widest action + 16. */
  rowActionsWidth?: number;
  /** Visual state per row: 'dim' (terminal states at 55%) or 'conflict'. */
  rowState?: (row: R) => 'dim' | 'conflict' | 'warn' | undefined;
  loading?: boolean;
  skeletonRows?: number;
  refreshing?: boolean;
  /** Rendered instead of the body when rows is an empty array. */
  empty?: ReactNode;
  stickyFirstColumn?: boolean;
  /** Pin the `rowActions` column to the inline-end edge (default true). */
  stickyActions?: boolean;
}

/** True while the card's content is wider than the card (it scrolls sideways) — draws the pinned columns' edges. */
function useOverflowX(el: HTMLDivElement | null): boolean {
  const [over, setOver] = useState(false);
  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => setOver(el.scrollWidth - el.clientWidth > 1);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    check();
    return () => ro.disconnect();
  }, [el]);
  return over;
}

function colWidth(w: ColumnWidth | undefined): string | undefined {
  if (typeof w === 'number') return `${w}px`;
  return undefined; // flex / fr columns share the remaining width
}

function minTableWidth<R>(columns: Column<R>[], actionsWidth: number): number {
  let w = actionsWidth;
  for (const c of columns) w += typeof c.width === 'number' ? c.width : c.minWidth ?? 140;
  return w;
}

export function DataTable<R>({
  columns,
  rows,
  rowKey,
  'aria-label': ariaLabel,
  density = 'default',
  footer,
  selectedKey,
  onRowClick,
  rowActions,
  rowActionsWidth = 96,
  rowState,
  loading,
  skeletonRows = 6,
  refreshing,
  empty,
  stickyFirstColumn = true,
  stickyActions = true,
}: DataTableProps<R>) {
  // callback ref: the scroll box mounts late when the first render is the empty state
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const overflowX = useOverflowX(scrollEl);
  const hasActions = !!rowActions;
  const lastIndex = columns.length - 1;
  // Exactly one pinned end column: the rowActions cell, else a last column marked stickyEnd.
  const stickyEndCol = !hasActions && lastIndex > 0 && columns[lastIndex].stickyEnd ? lastIndex : -1;
  const actionsSticky = hasActions && stickyActions;
  const hasStickyEnd = actionsSticky || stickyEndCol >= 0;
  const minWidth = minTableWidth(columns, hasActions ? rowActionsWidth : 0);
  const isEmpty = !loading && Array.isArray(rows) && rows.length === 0;

  if (isEmpty && empty) return <>{empty}</>;

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, row: R) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onRowClick(row);
    }
  };

  const cellClass = (c: Column<R>, i: number) =>
    [
      'e-td',
      `e-al-${c.align ?? 'start'}`,
      c.ltr ? 'ltr' : '',
      i === 0 && stickyFirstColumn ? 'e-sticky' : '',
      i === stickyEndCol ? 'e-sticky-end' : '',
    ]
      .filter(Boolean)
      .join(' ');
  const actionsClass = (base: string) => (actionsSticky ? `${base} e-sticky-end` : base);

  return (
    <div className="e-tbl cq" data-density={density} data-sticky-end={hasStickyEnd ? 'true' : undefined} data-overflow={overflowX ? 'true' : undefined} aria-busy={loading || refreshing || undefined}>
      <div className="e-tbl-scroll" ref={setScrollEl}>
        <table style={{ minWidth }} aria-label={ariaLabel}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: colWidth(c.width) }} />
            ))}
            {hasActions && <col style={{ width: rowActionsWidth }} />}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={c.key} scope="col" className={cellClass(c, i).replace('e-td', 'e-th').replace(' ltr', '')}>
                  {c.label}
                </th>
              ))}
              {hasActions && (
                <th scope="col" className={actionsClass('e-th')}>
                  <span className="sr-only">إجراءات</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, r) => (
                  <tr key={`sk-${r}`} className="e-tr" data-skeleton="true">
                    {columns.map((c, i) => (
                      <td key={c.key} className={cellClass(c, i)}>
                        <Skeleton width={c.align === 'end' ? '60%' : `${55 + ((r * 7 + i * 13) % 35)}%`} height={12} />
                      </td>
                    ))}
                    {hasActions && <td className={actionsClass('e-td')} />}
                  </tr>
                ))
              : (rows ?? []).map((row, idx) => {
                  const key = rowKey(row);
                  const state = rowState?.(row);
                  return (
                    <tr
                      key={key}
                      className="e-tr"
                      aria-selected={selectedKey != null ? key === selectedKey : undefined}
                      data-state={state}
                      data-clickable={onRowClick ? 'true' : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={onRowClick ? (e) => onKey(e, row) : undefined}
                    >
                      {columns.map((c, i) => (
                        <td key={c.key} className={cellClass(c, i)}>
                          {c.render ? c.render(row, idx) : (row as Record<string, ReactNode>)[c.key]}
                        </td>
                      ))}
                      {hasActions && (
                        <td className={actionsClass('e-td e-al-end e-td-actions')} onClick={(e) => e.stopPropagation()}>
                          {rowActions?.(row)}
                        </td>
                      )}
                    </tr>
                  );
                })}
          </tbody>
          {(footer || refreshing) && !loading && (
            <tfoot>
              <tr className="e-tfoot">
                {columns.map((c, i) => (
                  <td key={c.key} className={cellClass(c, i)}>
                    <Fragment>
                      {i === 0 && refreshing && <RefreshDot />}
                      {footer?.[c.key]}
                    </Fragment>
                  </td>
                ))}
                {hasActions && <td className={actionsClass('e-td')} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
